// ================= DECK LEGALITY, PROMOTED TO SHIPPED CODE =================
//
// Until now "what is a legal deck" existed ONLY as test assertions. js/cards.js says it outright:
// "nothing at runtime enforces deck construction; the precon decks in decks.js are simply authored
// correctly." That was fine while every deck in the game was authored by us and checked by
// tests/deck-legality-test.js before it shipped. It stops being fine the moment a player builds
// one: a deck the player assembles is not covered by any test that ran before release, so the rule
// has to travel with the game.
//
// PURE FUNCTIONS OVER window.CARDS. No DOM, no storage, no fetch -- so this file loads in plain
// node behind the `global.window = global` fixture the narrative tests use, and its own test drives
// it directly rather than through a rendered page.
//
// A DELIBERATE DUPLICATION, recorded here because it looks like something to clean up and is not.
// tests/deck-legality-test.js checks the fifteen shipped decks by comparing multisets against
// deploy_handoff/speed-edition/decks_contact_commitment_v4.json, and it must NOT be refactored to
// call this module. Its independence is the whole point: a validator bug shared by the builder and
// by the test that checks the builder passes silently in both. Two implementations that agree are
// evidence; one implementation checked against itself is not. This module gets its own test file.
//
// WHY FOREIGN_STYLE AND FOREIGN_FAMILY ARE CORRECTNESS RATHER THAN FLAVOUR. DECK_META[id].style
// drives automatic Style-passive dispatch in engine.js -- a fighter whose deck says WingChun gets
// the WingChun passive whatever cards are actually in the list. So a deck that mixes another
// Style's signature cards in does not merely look odd: it grants passives those cards were never
// built around, and the engine will not notice. Universal cards are legal in every deck by
// definition, which is what the tier means.
(function () {
  'use strict';

  const DECK_SIZE = 30;

  // The two formats, as data rather than as branches, so a caller can ASK what a format requires
  // (the builder prints "30 cards, up to 2 copies of any card" from these fields) instead of the
  // rule living only inside the validator's if-statements.
  //
  // `custom` is the player format: 30 cards, at most two copies of anything, and MINIMUMS on the two
  // owned tiers rather than an exact split. The two-copy allowance is the point of the format -- a
  // singleton pool leaks information, because a card you have seen played is a card provably not
  // coming back.
  //
  // WHY THERE ARE MINIMUMS AT ALL, since deck_builder/handoff.md argued against a ratio. Two things
  // it did not account for:
  //
  //   1. ITS ARITHMETIC ASSUMED SINGLETONS. The case against a strict 12/10/8 was that it "forces
  //      12 of 13 Style cards" -- C(13,12) = 13 selections, so the builder would really be a
  //      Universal-card picker. But this format allows TWO copies, and 12 Style slots drawn from 13
  //      cards with multiplicity is 201,643 distinct selections, not 13. The premise does not
  //      survive the format's own copy rule.
  //   2. A STYLELESS DECK STILL GETS THE PASSIVE. engine.js resolves a fighter's automatic passive
  //      from `fighter.style` alone -- via its Foundation, in makeFighter -- and never looks at the
  //      cards. With no floor, 30 Universal cards, the one tier every Style shares, was a legal
  //      "Western Boxing" deck collecting that Foundation's passive for free: pick the strongest
  //      passive, then build entirely from the pool it has no claim on. That is what the floor
  //      closes, and it is the same harm FOREIGN_STYLE prevents, arriving from the other side.
  //      The move to Foundation passives narrows the prize from eight choices to four and does not
  //      remove it -- the floor is still what makes the style declaration mean something.
  //
  // THE NUMBERS ARE THE SHIPPED DECKS', not a preference expressed here. All fifteen sit inside
  // Style 12-15, Family 8-10, Universal 6-8, so `Style >= 12, Family >= 8` is the envelope the
  // game's own decks already occupy and every one of them passes unchanged. Minimums rather than an
  // exact split because an exact 12/10/8 would forbid the four supplemental shapes the game itself
  // ships (15/9/6, 14/8/8, 14/10/6) -- it would contain decks a player is not allowed to build.
  //
  // Universal is bounded by implication (30 - 12 - 8 = 10) rather than by its own rule, so there is
  // one number to change per tier and no way for the three to contradict each other.
  //
  // `preconstructed` USED TO BE HERE and was retired on 2026-08-22 with the lists it described.
  // It held the shipped decks to the archive's §18 shape: single copies and an exact
  // 12 Style / 10 Family / 8 Universal split, so they could be run through this validator as a
  // check that it agreed with reality. The deck overhaul remade all ten baselines as 2-copy lists
  // legal under `custom` -- deliberately, so a baseline is an EXAMPLE of what the deck builder can
  // build rather than a shape only a shipped deck can have -- and a format that no deck in the game
  // is held to is a rule the validator would go on enforcing against nothing.
  //
  // The four supplementals were already `custom`, so every player deck now shares one envelope:
  // 30 cards, at most 2 copies, at least 12 Style and 8 Family slots. That is the envelope the
  // builder shows a player, which is the point.
  const FORMATS = {
    custom: {
      label: 'Custom',
      size: DECK_SIZE,
      maxCopies: 2,
      ratio: null,
      minTiers: { Style: 12, Family: 8 }
    },
    // WHAT AN AUTHORED CHALLENGE DECK IS HELD TO. Thirty cards, two copies, and that is the whole
    // rule -- because the two rules the other formats add both describe a PLAYER's collection and
    // neither is true of a boss.
    //
    // `allowsChallengeCards` is the only new field, and it names the exemption rather than hiding it
    // in a branch: a challenge deck is the one deck a Challenge-tier card is FOR, so reporting
    // CHALLENGE_ONLY against it would be the validator disagreeing with the design. It is a
    // whitelist of exactly one format, so the default for anything else -- including any format
    // added later that forgets to think about it -- stays "no".
    //
    // NO RATIO AND NO MINIMUMS, deliberately. The Beach Bum runs 12 Style / 10 Family / 4 Universal
    // / 4 Challenge, which no ratio written over the three owned tiers can express without either
    // counting Challenge cards as Universal (a lie the builder would then inherit) or hard-coding a
    // fourth tier into a rule players are shown. The real composition contract travels with the
    // deck, in its own test, where it can be stated exactly.
    challenge: {
      label: 'Daily Challenge',
      size: DECK_SIZE,
      maxCopies: 2,
      ratio: null,
      minTiers: null,
      allowsChallengeCards: true,
      // AND A BOSS MAY CROSS-TRAIN, which is the third exemption and the one that needed a
      // finding to justify it rather than a principle. Two of the Challenge Rotation decks pair
      // a Style with a Foundation that is not that Style's: Last-Train Southpaw is Western
      // Boxing running Kinetic Impact, and the Bell-Ringer Strongman is Wrestling running Torque
      // & Pressure. The designer built both that way on purpose -- each deck's ten Family cards
      // all belong to the Foundation it declares -- and no player could ever assemble one,
      // because legalPool derives the Family from the Style.
      //
      // WHAT IT COSTS, STATED SO NOBODY DISCOVERS IT LATER: the Foundation PASSIVE comes from
      // the Style (basePassiveIdFor in js/engine.js reads FAMILY_OF_STYLE), so those two
      // fighters resolve with a passive their ten Family cards were not built around. That is a
      // balance fact rather than a bug -- both still play, and tools/challenge-ladder.js measures
      // what they are actually worth -- but it is the exact hazard the FOREIGN_FAMILY rule was
      // written to catch, so it is exempted here by name rather than silently tolerated.
      //
      // FOREIGN_STYLE IS NOT EXEMPTED and must not be. That one really is correctness: a Style
      // card in the wrong deck gets passives it was never built around AND the engine cannot
      // notice. Every one of the twelve passes it.
      allowsForeignFamily: true
    }
  };

  const TIERS = ['Style', 'Family', 'Universal'];

  // THE WORD A PLAYER READS, WHICH IS NOT THE WORD THE DATA USES. `Family` is the identifier in card
  // data, in this validator, in every saved deck and every share code, and it stays that way --
  // renaming the data would invalidate stored decks for a word. But the builder's headings, counts
  // and summaries all say "Foundation" (js/card-tile.js owns that label, for the reason recorded
  // there: "Family" already means two other things in this game, an Attack Type and a Defense Type).
  // The refusals coming out of this file said "Family", so the validator was answering in a
  // vocabulary the page it validates does not use: a player reading "8 more Family cards needed"
  // had nothing on screen called Family to go and find.
  //
  // ITS OWN MAP RATHER THAN A CALL INTO CardTile, even though card-tile.js loads before this file on
  // all three pages. deck-rules.js is pure logic that the suites load standalone in plain node, with
  // no UI module present -- reaching for one would mean the messages read differently in the place
  // they are tested than in the place they ship, which is the split this fix exists to close, not to
  // move. tests/deck-rules-test.js holds the two equal instead: the repo's mirror pattern, as
  // js/store-catalog.js mirrors lib/store/catalog.js.
  //
  // USED ONLY WHERE MESSAGES ARE BUILT. Nothing below reads it to decide anything.
  const TIER_WORD = { Family: 'Foundation' };
  function tierWord(tier) { return TIER_WORD[tier] || tier; }

  // ---------------- cards a player may never build with ----------------
  //
  // AUTHORED BEFORE THE FIRST SUCH CARD EXISTS, which is the whole point. The Daily Challenge is
  // heading for authored challenge decks holding seasonal abilities no player deck may contain
  // (accounts-stage2-scope.md §6.3), and `legalPool` below is permissive BY CONSTRUCTION: it walks
  // window.CARDS and keeps everything whose tier is Universal, or Family in the right family, or
  // Style for this style. There is no notion of a card the engine resolves but the builder never
  // offers — so adding one today would put it in front of players the same day, not as a crash but
  // as a card in a column, which is the kind of thing that ships.
  //
  // `Challenge` is that notion, and it fails SAFE: an unrecognised tier already falls out of all
  // three branches of legalPool, so exclusion is the default and this constant exists to say so out
  // loud, to be assertable, and to give validate() a message better than "unknown card".
  const CHALLENGE_TIER = 'Challenge';

  // ---------------- decks a player may never browse ----------------
  //
  // THE DECK-LEVEL HALF OF THE SAME IDEA, and it is a separate rule rather than a consequence of
  // the one above, because the two hide different things and one does not imply the other. A
  // Challenge CARD is missing from the builder's pool; a concealed DECK is missing from the
  // pickers and from every browser that would list its contents. A challenge deck wants both --
  // seasonal cards nobody can build with, in a list nobody can read ahead of time -- but a deck
  // made entirely of ordinary cards can still be concealed (a boss whose thirty familiar cards are
  // a surprise in their arrangement), and a Challenge card would still be unbuildable if it turned
  // up in a deck that was not.
  //
  // WHAT CONCEALED MEANS, exactly, because "hidden" is vaguer than the code can afford:
  //
  //   NOT OFFERED. It is in no deck select, no lobby list, no clone menu, and it is never the
  //   random opponent. The only thing that can put a player in front of it is a mode that names it
  //   deliberately -- today that is the Daily Challenge and nothing else.
  //   NOT BROWSABLE. Its thirty cards are not listed anywhere outside a bout. The setup preview,
  //   the phone deck sheet and the builder's clone all refuse it by name rather than rendering an
  //   empty list, which is the difference between a sealed door and a broken one.
  //   STILL PLAYED, AND STILL SEEN. Nothing here touches the engine or the bout screen. Its cards
  //   telegraph, commit, resolve and read exactly like any others -- meeting them one at a time
  //   across an exchange IS the feature, and a deck the player can never see the contents of would
  //   be a different and much worse one.
  //
  // FAILS SAFE IN THE OTHER DIRECTION FROM THE CARD RULE, deliberately. An unrecognised card tier
  // falls out of legalPool by default, so a card is hidden unless something says otherwise; a deck
  // with no `concealed` flag is a normal deck, because every deck that exists today is one and
  // defaulting fifteen shipped decks to invisible is not a safe failure, it is an empty game. The
  // flag is therefore explicit, and tests/concealed-deck-test.js asserts that the shipped fifteen
  // do not carry it.
  function deckMeta() { return window.DECK_META || {}; }

  function isConcealedDeck(deckId) {
    const meta = deckMeta()[deckId];
    return !!(meta && meta.concealed);
  }

  // Every deck a player may be OFFERED, in DECK_META's own order -- which is the shipped order with
  // the player's saved decks merged on the end, and is what the selects have always shown.
  //
  // Callers filter through this rather than each writing `.filter(id => !meta.concealed)`, because
  // there are six such lists across three files and the one that gets forgotten is exactly how a
  // seasonal deck ends up in a dropdown a week before its season.
  function openDeckIds() {
    return Object.keys(deckMeta()).filter(id => !isConcealedDeck(id));
  }

  function cardsTable() { return window.CARDS || {}; }
  function styleList() { return window.STYLES || []; }
  function familyOfStyle(style) { return (window.FAMILY_OF_STYLE || {})[style] || null; }

  // { [cardId]: copies }, in first-seen order -- which is stable for a given list and is what makes
  // the issue array deterministic without a sort.
  function copyCounts(cardIds) {
    const out = Object.create(null);
    (cardIds || []).forEach(id => { out[id] = (out[id] || 0) + 1; });
    return out;
  }

  // { Style, Family, Universal, unknown }. `unknown` counts ids with no card behind them rather
  // than dropping them, because a 30-card list that is 4 cards short of any real tier is a
  // different problem from a 26-card list and the builder shows both.
  function tierCounts(cardIds) {
    const C = cardsTable();
    const out = { Style: 0, Family: 0, Universal: 0, unknown: 0 };
    (cardIds || []).forEach(id => {
      const card = C[id];
      if (!card || TIERS.indexOf(card.ownershipTier) < 0) out.unknown++;
      else out[card.ownershipTier]++;
    });
    return out;
  }

  // ---------------- cards a player must BUY before building with ----------------
  //
  // A THIRD KIND OF EXCLUSION, and it is not either of the two above. A Challenge card is barred
  // from every player deck forever; a concealed deck is barred from every list. A STORE card is
  // barred from nothing — it is an ordinary Style/Family/Universal card in every respect — until
  // this particular player has bought it, at which point it is simply theirs. So it cannot be a
  // property of the card, and it cannot be a property of the deck: it is a property of the pair.
  //
  // Which is why `ownedSet` is an ARGUMENT rather than a global this module reads. DeckRules is
  // pure functions over window.CARDS (see the header) and it stays that way: no fetch, no session,
  // no opinion about whether accounts exist. The caller knows who is asking; this knows the rule.
  //
  // AND `undefined` MEANS "NOT SUPPLIED", WHICH IS NOT THE SAME AS "OWNS NOTHING". A file:// copy,
  // a signed-out player and a page that has not asked the server yet all pass nothing — and for all
  // three the honest pool is the base 194 with the seasonal cards ABSENT, exactly as if the season
  // did not exist. An empty owned set says the same thing here, so the two agree; the distinction
  // that matters is the one the LIBRARY SCREEN draws, where "no session" hides the store entirely
  // and "signed in, owns none" shows fifteen cards with prices on them. A lock with no unlock path
  // is an ad for a feature that build does not have.
  // IT FAILS CLOSED, and the first version of this failed open — which is worth recording, because
  // open looked like the careful choice at the time.
  //
  // The catalog is a script a page has to remember to load. The original rule was "no catalog, no
  // store cards", reasoning that a file:// build with no store must stay fully buildable. It does —
  // but so does one where the seasonal cards are simply ABSENT, which is the same answer a
  // signed-out player already gets. The two readings only differ in their failure mode, and they
  // differ completely: forget the script on one page and the open version hands every player the
  // whole season for free, silently, on that page alone. tests/deck-rules-test.js found it by
  // crashing on a share code, which is not where anyone would have gone looking.
  //
  // So a card is a store card if the CATALOG says so, or — with no catalog to ask — if the card
  // itself is seasonal and belongs to a tier a player can own. The second clause needs nothing
  // loaded and cannot be forgotten. The AI-only props carry `seasonal` too and are excluded by
  // their Challenge tier, which is already the stricter rule.
  function isStoreCard(id) {
    if (window.STORE_CATALOG) return !!window.STORE_CATALOG.isForSale(id);
    const card = cardsTable()[id];
    return !!(card && card.seasonal && TIERS.indexOf(card.ownershipTier) >= 0);
  }

  // Whether THIS player may build with `id`, given what they own. Everything that is not a store
  // card answers yes, which keeps the base pool exactly as permissive as it has always been.
  function ownsCard(id, ownedSet) {
    if (!isStoreCard(id)) return true;
    if (!ownedSet) return false;
    return typeof ownedSet.has === 'function' ? ownedSet.has(id) : ownedSet.indexOf(id) >= 0;
  }

  // Every card a deck of this Style may legally contain, split by tier and sorted by DISPLAY NAME
  // rather than by id -- the builder renders these as a browsable column and `axe_kick` sorting
  // between `arm_spin` and `back_leg_roundhouse` is an id order, not a reading order.
  //
  // `ownedSet` (optional): a Set or array of card ids this player has bought. Store cards outside
  // it are left out of the pool entirely — not greyed, not listed as unavailable, absent. The
  // builder's job is the thirty slots; buying happens on the library screen and nowhere else.
  function legalPool(style, ownedSet) {
    const C = cardsTable();
    const family = familyOfStyle(style);
    const out = { Style: [], Family: [], Universal: [] };
    Object.keys(C).forEach(id => {
      const card = C[id];
      if (!ownsCard(id, ownedSet)) return;
      if (card.ownershipTier === 'Universal') out.Universal.push(id);
      else if (card.ownershipTier === 'Family' && card.ownershipGroup === family) out.Family.push(id);
      else if (card.ownershipTier === 'Style' && card.style === style) out.Style.push(id);
    });
    const byName = (a, b) => String((C[a] || {}).name || a).localeCompare(String((C[b] || {}).name || b));
    TIERS.forEach(t => out[t].sort(byName));
    return out;
  }

  // -> [] when legal, else [{ code, message, cardId? }].
  //
  // ALWAYS AN ARRAY, NEVER A THROW for anything a deck can be wrong about, because the builder
  // calls this on every keystroke and renders the result live. The one exception is an unrecognised
  // `format`, which is not a property of the deck at all -- it is the calling code passing a string
  // this module does not define, and silently treating it as 'custom' would loosen every rule for
  // whoever typed 'Custom' or 'standard' by mistake.
  //
  // ORDER IS DELIBERATE: whole-deck problems (style, size, ratio) first, then per-card ones in the
  // list's own first-seen order. The builder prints the first issue largest, and "this deck is 17
  // cards" is the thing to say before "and one of them is the wrong Style".
  //
  // `ownedSet` (optional, 4th argument): what this player has bought. A store card outside it is
  // reported as UNOWNED with its name and price, rather than as UNKNOWN_CARD — the card exists, the
  // deck is not illegal in any permanent sense, and the fix is a purchase rather than an edit. The
  // argument is LAST and optional so every existing caller keeps its exact behaviour: with nothing
  // supplied, a deck holding a store card fails as UNOWNED, which is the right answer for a
  // file:// build and for a saved deck that arrived from somewhere else.
  function validate(cardIds, style, format, ownedSet) {
    const spec = FORMATS[format];
    if (!spec) {
      throw new TypeError('DeckRules.validate: unknown format ' + JSON.stringify(format) +
        ' (expected one of: ' + Object.keys(FORMATS).join(', ') + ')');
    }
    const C = cardsTable();
    const ids = cardIds || [];
    const issues = [];
    const add = (code, message, cardId) => {
      const issue = { code: code, message: message };
      if (cardId) issue.cardId = cardId;
      issues.push(issue);
    };

    // A style this game does not have makes the two ownership checks meaningless -- every Style
    // card in the list would report FOREIGN_STYLE and bury the one issue that matters. So it is
    // reported once and those two checks are skipped; size, copies and unknown ids still run,
    // because none of them depends on the Style.
    const styleKnown = styleList().indexOf(style) >= 0;
    if (!styleKnown) {
      add('BAD_STYLE', JSON.stringify(style) + ' is not one of the eight Styles.');
    }

    if (ids.length !== spec.size) {
      const short = spec.size - ids.length;
      add('BAD_SIZE', short > 0
        ? short + ' more ' + (short === 1 ? 'card' : 'cards') + ' needed (' + ids.length + ' of ' + spec.size + ').'
        : Math.abs(short) + ' too many (' + ids.length + ' of ' + spec.size + ').');
    }

    if (spec.ratio) {
      const t = tierCounts(ids);
      const wrong = TIERS.filter(k => t[k] !== spec.ratio[k]);
      if (wrong.length) {
        add('BAD_RATIO', 'This format is exactly ' +
          TIERS.map(k => spec.ratio[k] + ' ' + tierWord(k)).join(' / ') + '; this deck is ' +
          TIERS.map(k => t[k] + ' ' + tierWord(k)).join(' / ') + '.');
      }
    }

    // ONE ISSUE PER UNMET TIER, not one combined. "3 more Style cards needed" and "3 more
    // Foundation cards needed" are two different things to go and do, and the builder prints the
    // list; a single line saying the deck is off-ratio makes the player work out which tier and by
    // how much.
    if (spec.minTiers) {
      const t = tierCounts(ids);
      TIERS.forEach(tier => {
        const min = spec.minTiers[tier];
        if (!min || t[tier] >= min) return;
        const short = min - t[tier];
        add('BAD_RATIO', short + ' more ' + tierWord(tier) + ' ' + (short === 1 ? 'card' : 'cards') +
          ' needed (' + t[tier] + ' of ' + min + ').');
      });
    }

    const counts = copyCounts(ids);
    const family = familyOfStyle(style);
    Object.keys(counts).forEach(id => {
      const card = C[id];
      if (!card) {
        add('UNKNOWN_CARD', 'No card called ' + JSON.stringify(id) + ' exists.', id);
        return;
      }
      if (counts[id] > spec.maxCopies) {
        add('OVER_COPIES', card.name + ': ' + counts[id] + ' copies, and this format allows ' +
          spec.maxCopies + '.', id);
      }
      // Checked before the style rules and independently of `styleKnown`: a Challenge card is
      // illegal in every deck of every style, so gating it behind a known style would let one
      // through in exactly the deck that is already confused about what it is.
      //
      // The `challenge` format is the single exemption, and it is read off the spec rather than
      // written as `format !== 'challenge'` -- the question is what this format ALLOWS, and a
      // string comparison here would have to be found and updated by anyone adding a second
      // authored format.
      // Checked before the Challenge rule and independently of it, because the two answer different
      // questions and only one of them has a remedy. A store card the player has not bought is a
      // card they CAN have; saying "this belongs to a Daily Challenge deck" about it would be
      // false, and saying nothing would let it through.
      if (isStoreCard(id) && !ownsCard(id, ownedSet)) {
        const price = window.STORE_CATALOG ? window.STORE_CATALOG.priceOf(id) : null;
        add('UNOWNED', card.name + ' is a seasonal card you have not unlocked yet' +
            (price === null ? '.' : ' (' + price + ' credits).'), id);
      }
      if (card.ownershipTier === CHALLENGE_TIER && !spec.allowsChallengeCards) {
        add('CHALLENGE_ONLY', card.name + ' belongs to a Daily Challenge deck and cannot be built ' +
          'into your own.', id);
      }
      if (!styleKnown) return;
      if (card.ownershipTier === 'Style' && card.style !== style) {
        add('FOREIGN_STYLE', card.name + ' is a ' + label(card.style) + ' card.', id);
      }
      if (card.ownershipTier === 'Family' && card.ownershipGroup !== family &&
          !spec.allowsForeignFamily) {
        add('FOREIGN_FAMILY', card.name + ' belongs to ' + label(card.ownershipGroup) + '.', id);
      }
    });

    return issues;
  }

  function label(key) { return (window.STYLE_LABELS || {})[key] || key; }

  // CAN THIS DECK STILL REACH A LEGAL RATIO IF ONE MORE CARD OF `tier` GOES IN?
  //
  // The builder needs this to grey out a + before the mistake rather than after it: with a floor of
  // 12 Style and 8 Family, a player who fills 20 slots with Universal cards has already lost, and
  // finding that out at card 30 means undoing a lot of work. Reachability, not legality -- an empty
  // deck reaches everything.
  //
  // Lives here rather than in the builder because it is the same rule as validate()'s, read
  // forwards, and two readings of one rule in two files is how they drift.
  //
  // -> { ok: true } | { ok: false, reason }
  function canAddTier(cardIds, tier, format) {
    const spec = FORMATS[format];
    if (!spec) {
      throw new TypeError('DeckRules.canAddTier: unknown format ' + JSON.stringify(format));
    }
    const ids = cardIds || [];
    if (ids.length >= spec.size) {
      return { ok: false, reason: 'The deck is full at ' + spec.size + ' cards.' };
    }
    if (!spec.minTiers) return { ok: true };
    const t = tierCounts(ids);
    t[tier] = (t[tier] || 0) + 1;
    const slotsLeft = spec.size - (ids.length + 1);
    let stillNeeded = 0;
    const shortfalls = [];
    TIERS.forEach(k => {
      const min = spec.minTiers[k];
      if (!min) return;
      const gap = Math.max(0, min - t[k]);
      stillNeeded += gap;
      if (gap) shortfalls.push(gap + ' more ' + tierWord(k));
    });
    if (stillNeeded > slotsLeft) {
      return { ok: false,
               reason: 'Adding this would leave too few slots for the ' +
                       TIERS.filter(k => spec.minTiers[k])
                            .map(k => spec.minTiers[k] + ' ' + tierWord(k)).join(' / ') +
                       ' minimum (' + shortfalls.join(' and ') + ' needed, ' + slotsLeft +
                       ' slot' + (slotsLeft === 1 ? '' : 's') + ' left).' };
    }
    return { ok: true };
  }

  // ---------------- The share-code manifest ----------------
  //
  // FROZEN AT SHIP AND NEVER EDITED. Share codes (phase 4) encode a deck as 30 INDEXES into this
  // array, which is the whole reason it is written out longhand instead of computed from
  // `Object.keys(window.CARDS).sort()` at load time. A live sorted list re-sorts the moment a card
  // is added: insert one id beginning with 'a' and every index after it shifts by one, so every
  // share code anyone has ever posted silently decodes into a DIFFERENT 30 cards. Not an error --
  // a legal deck, made of the wrong cards.
  //
  // So when the pool grows, this array does not change. New cards simply cannot appear in a v1
  // code; ship CARD_MANIFEST_V2 as a superset and bump the code prefix to SCD2 at the same time.
  //
  // 194 ids, the pool as of the deck builder's first commit, sorted.
  const CARD_MANIFEST_V1 = [
    'adrenaline_surge', 'age_uke_counter', 'angle_step', 'ankle_pick', 'arm_drag', 'arm_spin',
    'axe_kick', 'back_leg_roundhouse', 'backstep', 'blast_double', 'blitzing_backfist',
    'body_fold', 'body_guard', 'body_head_combination', 'body_jab', 'body_lock',
    'body_roundhouse', 'bong_lap_cross', 'bong_sao', 'borrowed_momentum', 'break_the_base',
    'breath_control', 'calm_the_storm', 'catch_your_balance', 'centerline_entry',
    'chain_punches', 'chain_wrestling', 'chambered_feint', 'check_and_return', 'check_hook',
    'chi_sao', 'chopping_low_kick', 'circle_out', 'circular_entry', 'clinch_dump', 'clinch_knee',
    'collar_snap', 'collar_tie', 'cover_and_crash', 'crescent_kick', 'cross_counter', 'cut_kick',
    'cut_the_angle', 'de_ashi_barai', 'dempsey_roll', 'distance_tax', 'double_jab',
    'double_leg_takedown', 'downward_elbow', 'driving_side_kick', 'duckunder',
    'eight_limb_barrage', 'elbow_spike', 'eye_rake', 'feint_high', 'feint_low',
    'five_point_combination', 'flowing_counter', 'flowing_takedown', 'foot_stomp', 'foot_sweep',
    'frame_and_exit', 'front_snap_kick', 'gazelle_hook', 'glove_grab', 'grip_battle',
    'gunting_scissor', 'hand_fighting', 'hand_trap', 'handful_of_cloth', 'harai_goshi',
    'harimau_entry', 'headbutt', 'heavy_base', 'high_crotch_lift', 'high_guard', 'hip_position',
    'hip_throw', 'hook_kick', 'horizontal_elbow', 'horse_stance', 'inside_low_kick',
    'inside_trip', 'intercepting_front_kick', 'kiai_commitment', 'kinetic_recovery',
    'knee_block_takedown', 'knee_buckle_kick', 'knee_tap', 'knee_to_the_groin',
    'knife_hand_counter', 'kosoto_gake', 'langkah_angle', 'langkah_low_trap', 'lap_sao',
    'lead_leg_side_kick', 'level_change', 'lift_and_turn', 'limb_destruction', 'linear_blitz',
    'liver_hook', 'long_frame_structure', 'long_knee', 'low_line_trap', 'mae_geri',
    'marching_pressure', 'mat_return', 'mawashi_geri', 'measured_entry', 'oblique_kick',
    'off_line_entry', 'oi_zuki', 'one_strike_finish', 'one_two', 'osoto_gari', 'ouchi_gari',
    'outside_blade', 'outside_reap', 'outside_trip', 'overhand_right', 'overhand_step',
    'pak_sao', 'philly_shell', 'piston_jab', 'pivot_away', 'pivot_cross', 'plant_and_fire',
    'pressure_rhythm', 'probe_jab', 'push_side_kick', 'question_mark_kick', 'rabbit_punch',
    'rapid_rechamber', 'read_the_hips', 'rear_body_lock', 'recover_guard', 'redirecting_frame',
    'repeating_roundhouse', 'reset_distance', 'retreating_roundhouse', 'reverse_punch',
    'ridge_hand_strike', 'rising_low_blow', 'rooted_extension', 'rope_a_dope', 'rotational_load',
    'roundhouse_kick', 'russian_tie_snap', 'sacrifice_roll', 'sacrifice_sweep', 'sapu_sweep',
    'sasae_tsurikomi_ashi', 'second_wind', 'sensitivity_read', 'seoi_nage', 'shoulder_wheel',
    'shovel_hook', 'sickle_kick', 'simultaneous_block_and_strike', 'single_leg_run',
    'skip_step_roundhouse', 'slipping_cross', 'snap_down', 'spinning_back_kick',
    'sprawl_and_frame', 'sprawl_and_spin', 'step_in', 'stiff_jab', 'sweeping_heel_kick',
    'sweeping_side_kick', 'switch_body_kick', 'switch_kick', 'tai_otoshi', 'tan_sao', 'teep',
    'thai_plum', 'thrown_sand', 'thrust_kick', 'thumb_jab', 'tight_shell', 'tomoe_nage',
    'tornado_kick', 'torque_release', 'trade_in_the_pocket', 'turning_side_kick', 'uchi_mata',
    'underhook_battle', 'ushiro_geri', 'watch_the_shoulders', 'weight_shift_counter',
    'wing_chun_side_kick', 'wooden_dummy_sequence', 'yoko_otoshi', 'zenkutsu_drive'
  ];

  // ---------------- share codes ----------------
  //
  // 'SCD1.' + base64url(JSON) where the JSON is
  //   { v: 1, n: name, d: description?, s: styleKey, a: artKey, c: [30 manifest indexes] }
  //
  // WHY THE CARDS ARE INDEXES AND THE STYLE AND ART ARE NOT. The thirty card slots are indexes into
  // CARD_MANIFEST_V1 because thirty ids would triple the code's length, and the manifest is frozen
  // precisely so those indexes keep meaning the same thing forever.
  //
  // deck_builder/implementation-plan.md §5 specifies `s: styleIndex, a: artIndex` as well. That is
  // wrong for the art and it is worth spelling out, because it is the same bug the card manifest
  // exists to prevent and it bites harder: an artIndex would index Object.keys(window.DECK_META),
  // which is INSERTION-ordered rather than sorted, and which phase 3 merges the player's own saved
  // decks into at boot. The index would therefore depend on how many custom decks the person doing
  // the ENCODING happened to have saved -- so two players sharing the same deck would produce
  // different codes, and a code would decode differently on the machine that made it after saving
  // one more deck. Not a release-boundary problem: a per-player, same-day one.
  //
  // Freezing two more manifests for two scalars buys nothing, so both travel as their KEYS. That is
  // about 25 characters against a ~280-character code, and it makes the payload survive any
  // reordering of STYLES or DECK_META, and be readable when something goes wrong. Only the cards
  // needed the compression.
  const SHARE_PREFIX = 'SCD1.';

  // UTF-8 and base64 by hand, in both directions, because this has to produce the SAME string in a
  // browser and in node. btoa throws on any character above U+00FF (a deck named "Café" is not
  // exotic), Buffer does not exist in a browser, and TextEncoder plus a base64 shim is more moving
  // parts than the twenty lines below. Hand-rolled means the test can assert a code byte for byte.
  const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

  function utf8Bytes(str) {
    const out = [];
    for (let i = 0; i < str.length; i++) {
      let c = str.charCodeAt(i);
      // Surrogate pair -> one code point, so astral characters (emoji in a deck name) survive.
      if (c >= 0xd800 && c <= 0xdbff && i + 1 < str.length) {
        const lo = str.charCodeAt(i + 1);
        if (lo >= 0xdc00 && lo <= 0xdfff) { c = 0x10000 + ((c - 0xd800) << 10) + (lo - 0xdc00); i++; }
      }
      if (c < 0x80) out.push(c);
      else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 63));
      else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
      else out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
    }
    return out;
  }

  function utf8String(bytes) {
    let out = '';
    for (let i = 0; i < bytes.length;) {
      const b = bytes[i++];
      let c;
      if (b < 0x80) c = b;
      else if ((b & 0xe0) === 0xc0) c = ((b & 31) << 6) | (bytes[i++] & 63);
      else if ((b & 0xf0) === 0xe0) c = ((b & 15) << 12) | ((bytes[i++] & 63) << 6) | (bytes[i++] & 63);
      else c = ((b & 7) << 18) | ((bytes[i++] & 63) << 12) | ((bytes[i++] & 63) << 6) | (bytes[i++] & 63);
      if (c > 0xffff) {
        c -= 0x10000;
        out += String.fromCharCode(0xd800 + (c >> 10), 0xdc00 + (c & 1023));
      } else out += String.fromCharCode(c);
    }
    return out;
  }

  // base64url: no padding, '-' and '_' for '+' and '/', so a code survives a URL, a chat client and
  // a double-click-to-select without being mangled.
  function toBase64Url(str) {
    const b = utf8Bytes(str);
    let out = '';
    for (let i = 0; i < b.length; i += 3) {
      const n = (b[i] << 16) | ((i + 1 < b.length ? b[i + 1] : 0) << 8) | (i + 2 < b.length ? b[i + 2] : 0);
      out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63];
      if (i + 1 < b.length) out += B64[(n >> 6) & 63];
      if (i + 2 < b.length) out += B64[n & 63];
    }
    return out;
  }

  function fromBase64Url(s) {
    const bytes = [];
    let acc = 0, bits = 0;
    for (let i = 0; i < s.length; i++) {
      const v = B64.indexOf(s[i]);
      if (v < 0) return null;              // any character outside the alphabet: not our code
      acc = (acc << 6) | v; bits += 6;
      if (bits >= 8) { bits -= 8; bytes.push((acc >> bits) & 255); }
    }
    return utf8String(bytes);
  }

  const MANIFEST_INDEX = (function () {
    const m = Object.create(null);
    CARD_MANIFEST_V1.forEach((id, i) => { m[id] = i; });
    return m;
  })();

  // -> { ok: true, code } | { ok: false, error, message }.
  //
  // A deck containing a card that is not in the v1 manifest CANNOT be encoded, and says so rather
  // than dropping the card. Silently shipping a 29-card deck under a name that promises 30 would be
  // the worst available outcome: it decodes cleanly on the other side, into a different deck.
  function encodeShareCode(deck) {
    if (!deck || !Array.isArray(deck.cards)) {
      return { ok: false, error: 'BAD_DECK', message: 'There is no deck to share yet.' };
    }
    const unlisted = deck.cards.filter(id => MANIFEST_INDEX[id] === undefined);
    if (unlisted.length) {
      return { ok: false, error: 'UNSHAREABLE',
               message: 'This deck uses ' + unlisted.length + ' card' + (unlisted.length === 1 ? '' : 's') +
                        ' that share codes cannot carry yet.' };
    }
    const payload = {
      v: 1,
      n: String(deck.name || '').slice(0, 28),
      s: deck.style,
      a: deck.artKey,
      c: deck.cards.map(id => MANIFEST_INDEX[id])
    };
    const desc = String(deck.description || '').slice(0, 100);
    if (desc) payload.d = desc;
    return { ok: true, code: SHARE_PREFIX + toBase64Url(JSON.stringify(payload)) };
  }

  // -> { ok: true, deck } | { ok: false, error, message, issues? }. NEVER THROWS. Everything it is
  // handed is a string somebody pasted, so every failure is a normal outcome with a sentence a
  // player can act on, not an exception for the page to fall over on.
  function decodeShareCode(text) {
    const raw = String(text == null ? '' : text).trim();
    if (!raw) return { ok: false, error: 'EMPTY', message: 'Paste a share code first.' };

    const dot = raw.indexOf('.');
    const prefix = dot >= 0 ? raw.slice(0, dot + 1) : '';
    if (!/^SCD\d+\.$/.test(prefix)) {
      return { ok: false, error: 'BAD_FORMAT',
               message: 'That does not look like a Sparring Cards deck code.' };
    }
    if (prefix !== SHARE_PREFIX) {
      // Named separately from a corrupt code because the fix is completely different: there is
      // nothing wrong with this code and nothing the player can do to it.
      return { ok: false, error: 'BAD_VERSION',
               message: 'This code is from a newer version of the game.' };
    }

    const json = fromBase64Url(raw.slice(dot + 1));
    if (json === null) return { ok: false, error: 'BAD_PAYLOAD', message: 'This code is damaged.' };
    let p;
    try { p = JSON.parse(json); } catch (e) { p = null; }
    if (!p || typeof p !== 'object' || !Array.isArray(p.c)) {
      return { ok: false, error: 'BAD_PAYLOAD', message: 'This code is damaged.' };
    }

    const cards = [];
    for (let i = 0; i < p.c.length; i++) {
      const idx = p.c[i];
      if (!Number.isInteger(idx) || idx < 0 || idx >= CARD_MANIFEST_V1.length) {
        return { ok: false, error: 'BAD_INDEX',
                 message: 'This code names a card this version of the game does not have.' };
      }
      cards.push(CARD_MANIFEST_V1[idx]);
    }

    const deck = {
      id: null,
      name: String(p.n || '').slice(0, 28),
      description: String(p.d || '').slice(0, 100),
      style: p.s,
      artKey: p.a,
      cards: cards
    };

    // The rules run LAST and their verdict is passed straight out. A code can be perfectly well
    // formed and describe an illegal deck -- a hand-edited payload, or a deck that was legal under
    // rules that have since changed -- and the player deserves the real reason rather than
    // "invalid code".
    const issues = validate(deck.cards, deck.style, FORMAT_FOR_SHARED);
    if (issues.length) {
      return { ok: false, error: 'ILLEGAL', issues: issues, deck: deck,
               message: 'This code decodes to a deck that is not legal: ' + issues[0].message };
    }
    // An art key the game does not recognise is not worth refusing a whole deck over -- the deck is
    // legal and playable, and art is presentation. Replaced rather than rejected, which is also what
    // makes a code from a build with MORE art still importable: you get the deck, with default art.
    // Checked against js/fighter-art.js rather than DECK_META, because art is no longer a property
    // of the deck list.
    if (!window.FIGHTER_ART || !window.FIGHTER_ART[deck.artKey]) deck.artKey = null;
    return { ok: true, deck: deck };
  }

  const FORMAT_FOR_SHARED = 'custom';

  window.DeckRules = {
    FORMATS: FORMATS,
    DECK_SIZE: DECK_SIZE,
    MAX_COPIES: FORMATS.custom.maxCopies,
    NAME_MAX: 28,
    DESC_MAX: 100,
    MAX_SAVED: 20,

    validate: validate,
    canAddTier: canAddTier,
    legalPool: legalPool,
    tierCounts: tierCounts,
    // Exported so a test can assert the tier by name rather than by a string literal that could
    // drift, and so js/daily-challenge.js can describe a challenge deck without re-inventing it.
    CHALLENGE_TIER: CHALLENGE_TIER,

    // The deck-level half. Every list that offers a deck to a player goes through one of these two.
    isConcealedDeck: isConcealedDeck,
    openDeckIds: openDeckIds,

    // The ownership half. Exported so the library screen and the builder ask the same two questions
    // this module already asks internally, rather than each re-deriving "is this for sale" from the
    // catalog and "do I have it" from a list — which is how a screen comes to show a lock on a card
    // the validator would happily accept.
    isStoreCard: isStoreCard,
    ownsCard: ownsCard,

    CARD_MANIFEST_V1: CARD_MANIFEST_V1,

    // Share codes. Pure functions, so they live here rather than in the builder UI.
    SHARE_PREFIX: SHARE_PREFIX,
    encodeShareCode: encodeShareCode,
    decodeShareCode: decodeShareCode
  };
})();
